import React from 'react';
import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    Card,
    Chip
} from '@mui/material';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    closestCenter,
    pointerWithin,
    rectIntersection,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { updateTask, addTaskToQueue } from '../../../store/reducers/tasksReducer';
import { $authHost } from '../../../http';
import { useAuth } from 'react-oidc-context';
import { allowedTransitions, CreatedDateIcon, DeadlineDateIcon, EditedDateIcon, PersonCustomIcon } from '../TasksView';

// Styled компоненты с мобильной адаптацией
const KanbanContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    width: '100%',
    // height: '100%',
    backgroundColor: darkMode ? '#1a1a1a' : '#f8fafc',
    [theme.breakpoints.down('md')]: {
        padding: '0.25rem',
    }
}));

const KanbanHeaderRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    display: 'flex',
    gap: '0.25rem',
    padding: '0.5rem 0.5rem 0.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: darkMode ? '#1a1a1a' : '#f8fafc',
    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    marginBottom: '0rem',
    width: '99%',
    [theme.breakpoints.down('md')]: {
        padding: '0.25rem 0.25rem 0.25rem',
        gap: '0.15rem',
        overflowX: 'visible',
        scrollSnapType: 'x mandatory',
        '&::-webkit-scrollbar': {
            height: '4px'
        }
    }
}));

const KanbanContentRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    display: 'flex',
    gap: '0.25rem',
    padding: '0.5rem 0.5rem 0.5rem',
    overflowX: 'visible',
    overflowY: 'visible',
    flex: 1,
    alignItems: 'flex-start',
    width: '99%',
    '&::-webkit-scrollbar': {
        height: '8px'
    },
    '&::-webkit-scrollbar-track': {
        background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
        background: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)'
    },
    [theme.breakpoints.down('md')]: {
        padding: '0.25rem 0.25rem 0.25rem',
        gap: '0.15rem',
        overflowX: 'visible',
        '&::-webkit-scrollbar': {
            height: '6px'
        }
    }
}));

const KanbanColumn = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode' && prop !== 'statuscolor',
})(({ theme, statuscolor, darkMode }) => ({
    borderRadius: '8px',
    padding: '0.2rem',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    backgroundColor: darkMode
        ? statuscolor + '20' // 12% opacity for dark mode
        : statuscolor + '15', // 8% opacity for light mode
    border: darkMode
        ? `1px solid ${statuscolor}40` // 25% opacity border
        : `1px solid ${statuscolor}30`, // 19% opacity border
    boxShadow: darkMode
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: darkMode
            ? statuscolor + '30' // 19% opacity on hover
            : statuscolor + '25', // 15% opacity on hover
        transform: 'translateY(-1px)',
        boxShadow: darkMode
            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(0, 0, 0, 0.15)'
    },
    [theme.breakpoints.down('md')]: {
        padding: '0.15rem',
        borderRadius: '6px',
        scrollSnapAlign: 'start',
        '&:hover': {
            transform: 'none',
            boxShadow: darkMode
                ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                : '0 1px 3px rgba(0, 0, 0, 0.1)'
        }
    },
    [theme.breakpoints.down('sm')]: {
        padding: '0.1rem'
    }
}));

const KanbanColumnHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode' && prop !== 'statuscolor',
})(({ theme, statuscolor, darkMode }) => ({
    padding: '0.5rem 1rem',
    marginBottom: '0.25rem',
    borderRadius: '5px',
    fontWeight: 700,
    color: '#fff',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    userSelect: 'none',
    backgroundColor: statuscolor,
    cursor: 'pointer',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: darkMode
        ? '0 2px 8px rgba(0, 0, 0, 0.4)'
        : '0 2px 4px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: darkMode
            ? '0 4px 12px rgba(0, 0, 0, 0.5)'
            : '0 4px 8px rgba(0, 0, 0, 0.3)',
        filter: 'brightness(1.1)'
    },
    '&:active': {
        transform: 'translateY(0)',
        boxShadow: darkMode
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    [theme.breakpoints.down('md')]: {
        padding: '0.4rem 0.8rem',
        fontSize: '0.75rem',
        gap: '0.3rem',
        '&:hover': {
            transform: 'none',
        }
    },
    [theme.breakpoints.down('sm')]: {
        padding: '0.3rem 0.6rem',
        fontSize: '0.7rem',
        letterSpacing: '0.3px'
    }
}));

const TasksContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    minHeight: '200px',
    padding: '0',
    borderRadius: '4px',
    backgroundColor: darkMode
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(255, 255, 255, 0.5)',
    border: darkMode
        ? '1px dashed rgba(255, 255, 255, 0.1)'
        : '1px dashed rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('md')]: {
        gap: '0.3rem',
        minHeight: '150px',
        padding: '0'
    },
    [theme.breakpoints.down('sm')]: {
        padding: '0'
    }
}));

const TaskCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    backgroundColor: darkMode ? '#2b2b2b' : '#fff',
    color: darkMode ? '#f5f7fa' : '#333',
    borderRadius: '8px',
    padding: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    boxShadow: darkMode
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.08)',
    cursor: 'grab',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: darkMode
        ? '1px solid rgba(255,255,255,0.12)'
        : '1px solid rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    position: 'relative',
    overflow: 'hidden',
    // '&::before': {
    //     content: '""',
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    //     right: 0,
    //     height: '2px',
    //     background: darkMode
    //         ? 'white'
    //         : 'black',
    //     opacity: 0,
    //     transition: 'opacity 0.3s ease'
    // },
    '&:hover': {
        boxShadow: darkMode
            ? '0 8px 24px rgba(0,0,0,0.4)'
            : '0 8px 24px rgba(0,0,0,0.12)',
        transform: 'translateY(0) scale(1.02)',
        borderColor: darkMode
            ? 'rgba(255,255,255,0.2)'
            : 'rgba(33, 150, 243, 0.2)',
        '&::before': {
            opacity: 1
        }
    },
    '&:active': {
        cursor: 'grabbing',
        transform: 'translateY(-2px) scale(1.01)'
    },
    [theme.breakpoints.down('md')]: {
        padding: '0.2rem',
        fontSize: '0.8rem',
        borderRadius: '6px',
        gap: '0.2rem',
        '&:hover': {
            transform: 'none',
            boxShadow: darkMode
                ? '0 4px 12px rgba(0,0,0,0.3)'
                : '0 4px 12px rgba(0,0,0,0.08)'
        }
    }
}));

const TaskName = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    fontWeight: 700,
    fontSize: '0.95rem',
    lineHeight: 1.1,
    color: darkMode ? '#f5f7fa' : '#1a1a1a',
    marginBottom: '0.25rem',
    [theme.breakpoints.down('md')]: {
        fontSize: '0.85rem',
        marginBottom: '0.2rem'
    }
}));

const TaskDesc = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    fontSize: '0.875rem',
    fontStyle: 'italic',
    lineHeight: 1.1,
    color: darkMode ? '#bbb' : '#666',
    marginBottom: '0.5rem',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('md')]: {
        fontSize: '0.8rem',
        WebkitLineClamp: 2,
        marginBottom: '0.3rem'
    }
}));

const MetaContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    fontSize: '0.75rem',
    color: darkMode ? '#999' : '#666',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.1rem',
    padding: '0.5rem 0',
    borderTop: darkMode
        ? '1px solid rgba(255,255,255,0.08)'
        : '1px solid rgba(0,0,0,0.08)',
    marginTop: '0rem',
    [theme.breakpoints.down('md')]: {
        padding: '0.3rem 0',
        gap: '0.05rem',
        fontSize: '0.7rem'
    }
}));

const MetaRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})(({ theme, darkMode }) => ({
    display: 'flex',
    justifyContent: 'start',
    width: '100%',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.75rem',
    color: darkMode ? '#999' : '#666',
    '& .MuiSvgIcon-root': {
        fontSize: '14px',
        opacity: 0.7
    },
    [theme.breakpoints.down('md')]: {
        gap: '0.2rem',
        fontSize: '0.7rem',
        '& .MuiSvgIcon-root': {
            fontSize: '12px'
        }
    }
}));

// Компонент строки метаданных
const MetaRowComponent = ({ icon: Icon, color, text, darkMode }) => (
    <MetaRow darkMode={darkMode}>
        {Icon && <Icon sx={{
            fontSize: { xs: '12px', md: '16px' }
        }} color={color} />}
        <Typography variant="caption" sx={{
            color: color || 'inherit',
            fontSize: { xs: '0.65rem', md: '0.75rem' },
            fontWeight: 600,
            opacity: 1,
            lineHeight: 1.1
        }}>
            {text}
        </Typography>
    </MetaRow>
);

// Компонент карточки задачи с поддержкой drag-and-drop
const SortableTaskCard = React.memo(({ task, isDragging, onEdit, onDelete, onViewSpecs, onFocusArea, darkMode, canEdit, canDrag, canDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging
    } = useSortable({
        id: task.id,
        data: {
            type: 'task',
            task
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging || isSortableDragging ? 0.6 : 1
    };

    return (
        <TaskCard
            ref={setNodeRef}
            style={style}
            darkMode={darkMode}
            sx={{
                // Особое оформление для неперетаскиваемых задач
                ...(!canDrag && {
                    opacity: 0.8,
                    border: darkMode
                        ? '1px dashed rgba(255,255,255,0.3)'
                        : '1px dashed rgba(0,0,0,0.3)',
                    cursor: 'default !important',
                    '&:hover': {
                        transform: 'none !important',
                        boxShadow: darkMode
                            ? '0 2px 8px rgba(0,0,0,0.3)'
                            : '0 2px 8px rgba(0,0,0,0.08)'
                    }
                })
            }}
            // Применяем обработчики перетаскивания на всю карточку
            {...(canDrag ? attributes : {})}
            {...(canDrag ? listeners : {})}
        >
            {/* Заголовок и индикатор просроченности */}
            <Box sx={{ userSelect: 'none' }}>
                <TaskName darkMode={darkMode} sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>{task.title || task.name}</TaskName>
                {task.isOverdueCopy && (
                    <Chip
                        label="Копия просроченной"
                        size="small"
                        sx={{
                            height: { xs: 14, md: 16 },
                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            border: '1px solid rgba(244, 67, 54, 0.3)',
                            mb: { xs: 0.3, md: 0.5 }
                        }}
                    />
                )}
            </Box>

            {/* Контент задачи */}
            <Box
                sx={{ cursor: canDrag ? 'grab' : 'default', userSelect: 'none', flex: 1 }}
            >
                {(task.description || task.desc) && (
                    <TaskDesc
                        darkMode={darkMode}
                        sx={{
                            fontSize: {
                                xs: '0.7rem',
                                md: '0.85rem'
                            },
                            '& strong, & b': {
                                fontWeight: 700,
                                color: darkMode ? '#fff' : '#1a1a1a'
                            },
                            '& em, & i': {
                                fontStyle: 'italic',
                                color: darkMode ? '#e0e0e0' : '#424242'
                            },
                            '& ul, & ol': {
                                paddingLeft: 2,
                                margin: '0.5rem 0',
                                color: darkMode ? '#bbb' : '#666'
                            },
                            '& li': {
                                listStyle: 'disc',
                                marginBottom: '0.25rem',
                                lineHeight: 1.3
                            },
                            '& ol li': {
                                listStyle: 'decimal'
                            },
                            '& code': {
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                padding: '0.2rem 0.4rem',
                                borderRadius: '0.25rem',
                                fontFamily: 'monospace',
                                fontSize: '0.85em',
                                color: darkMode ? '#ff6b6b' : '#d63384'
                            },
                            '& blockquote': {
                                borderLeft: `3px solid ${darkMode ? '#64b5f6' : '#1976d2'}`,
                                margin: '0.5rem 0',
                                paddingLeft: '0.75rem',
                                fontStyle: 'italic',
                                color: darkMode ? '#e3f2fd' : '#1565c0',
                                backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.05)'
                            },
                            '& h1, & h2, & h3': {
                                margin: '0.5rem 0 0.25rem 0',
                                fontWeight: 600,
                                color: darkMode ? '#fff' : '#1a1a1a'
                            },
                            '& h1': { fontSize: '1.1em' },
                            '& h2': { fontSize: '1.05em' },
                            '& h3': { fontSize: '1em' },
                            '& a': {
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            },
                            '& hr': {
                                border: 'none',
                                borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                                margin: '0.75rem 0'
                            },
                            '& pre': {
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                padding: '0.5rem',
                                borderRadius: '0.25rem',
                                overflow: 'auto',
                                fontSize: '0.8em',
                                fontFamily: 'monospace'
                            }
                        }}
                        dangerouslySetInnerHTML={{
                            __html: (task.description || task.desc)
                                // Заголовки
                                .replace(/^### (.*$)/gm, '<h3>$1</h3>')
                                .replace(/^## (.*$)/gm, '<h2>$1</h2>')
                                .replace(/^# (.*$)/gm, '<h1>$1</h1>')
                                // Жирный и курсив
                                .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                // Код
                                .replace(/`([^`]+)`/g, '<code>$1</code>')
                                .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
                                // Ссылки
                                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                                // Цитаты
                                .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
                                // Списки
                                .replace(/^(\d+)\. (.+)$/gm, '<oli>$2</oli>')
                                .replace(/^- (.+)$/gm, '<li>$1</li>')
                                // Обработка списков
                                .replace(/((<oli>.*<\/oli>\s*)+)/g, '<ol>$1</ol>')
                                .replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match}</ul>`)
                                .replace(/<oli>/g, '<li>')
                                .replace(/<\/oli>/g, '</li>')
                                // Горизонтальные линии
                                .replace(/^---$/gm, '<hr>')
                                .replace(/^\*\*\*$/gm, '<hr>')
                                // Переносы строк
                                .replace(/\n/g, '<br>')
                        }}
                    />
                )}
                <MetaContainer darkMode={darkMode}>
                    <MetaRowComponent darkMode={darkMode} icon={PersonCustomIcon} text={task.creator} color={'#7CB342'} />
                    <MetaRowComponent darkMode={darkMode} icon={CreatedDateIcon} text={formatDate(new Date(task.created_at))} color={'#7CB342'} />
                    {/* <MetaRowComponent darkMode={darkMode} icon={(() => {
                        return new Date().toDateString() <= new Date(task.deadline).toDateString() ? ResponsibleDeadlineIcon : ResponsibleIcon;
                    })()} text={task.responsible} /> */}

                    <MetaRowComponent darkMode={darkMode} icon={PersonCustomIcon} text={task.responsible} color={new Date().toDateString() <= new Date(task.deadline).toDateString() ? '#DC143C' : '#1976D2'} />
                    <MetaRowComponent
                        darkMode={darkMode}
                        icon={DeadlineDateIcon}
                        color={new Date().toDateString() <= new Date(task.deadline).toDateString() ? '#DC143C' : '#1976D2'}
                        text={formatDate(new Date(task.deadline))}
                    />
                    {task.editor && task.edited_at ? (<>
                        <MetaRowComponent darkMode={darkMode} icon={PersonCustomIcon} color={'#5F9EA0'} text={task.editor} />
                        <MetaRowComponent darkMode={darkMode} icon={EditedDateIcon} color={'#5F9EA0'} text={formatDate(new Date(task.edited_at))} />
                    </>) : null}
                </MetaContainer>
            </Box>

            {/* Нижняя панель с кнопками и ID */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0,
                pt: 0,
                px: 1,
                borderTop: darkMode
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(0,0,0,0.08)',
                userSelect: 'none'
            }}>
                {/* Кнопки слева */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    {/* Кнопка наведения на область карты (если есть опоры в задаче) */}
                    {task.data?.objects && task.data.objects.pillars && task.data.objects.pillars.length > 0 && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onFocusArea(task);
                            }}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#4fc3f7' : '#0277bd',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode
                                        ? 'rgba(79, 195, 247, 0.08)'
                                        : 'rgba(2, 119, 189, 0.04)'
                                }
                            }}
                            title="Показать на карте"
                        >
                            <LocationIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка просмотра материалов (если есть объекты в задаче) */}
                    {task.type === 'contour' && Object.keys(task.data.objects).some(type => task.data.objects[type]?.length > 0) && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewSpecs(task);
                            }}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#ce93d8' : '#9c27b0',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode
                                        ? 'rgba(156, 39, 176, 0.08)'
                                        : 'rgba(156, 39, 176, 0.04)'
                                }
                            }}
                        >
                            <AccountTreeIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}


                    {canEdit && !task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Для копий просроченных задач открываем редактирование исходной задачи
                                const taskToEdit = task.isOverdueCopy ?
                                    { ...task, id: task.originalId, status: task.originalStatus } :
                                    task;
                                onEdit(taskToEdit);
                            }}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                color: darkMode ? '#ddd' : '#333',
                                opacity: 0.8,
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: darkMode
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}
                    {canDelete && !task.isOverdueCopy && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Для копий просроченных задач удаляем исходную задачу
                                const taskIdToDelete = task.isOverdueCopy ? task.originalId : task.id;
                                onDelete(taskIdToDelete);
                            }}
                            sx={{
                                width: { xs: '20px', md: '24px' },
                                height: { xs: '20px', md: '24px' },
                                opacity: 0.8,
                                color: darkMode ? 'rgba(244, 67, 54, 1)' : 'rgba(244, 67, 54, 1)',
                                '&:hover': {
                                    opacity: 1,
                                    bgcolor: 'rgba(244, 67, 54, 0.08)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        </IconButton>
                    )}
                </Box>

                {/* ID задачи справа */}
                <Typography
                    variant="caption"
                    sx={{
                        color: darkMode ? '#888' : '#666',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        // fontFamily: 'monospace',
                        opacity: 0.9
                    }}
                >
                    id {task.isOverdueCopy ? task.originalId : task.id}
                </Typography>
            </Box>
        </TaskCard>
    );
});

// Компонент колонки с поддержкой drop
const DroppableColumn = React.memo(({ status, children, darkMode, draggedTask, sx }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: status.id,
        data: {
            type: 'column',
            status: status.id
        }
    });

    // Определяем статус исходной колонки из перетаскиваемой задачи
    const draggedTaskSourceStatus = draggedTask
        ? (draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status)
        : null;

    // Проверяем, можно ли перетащить задачу в эту колонку
    const isDropAllowed = !draggedTaskSourceStatus ||
        (allowedTransitions[draggedTaskSourceStatus]?.includes(status.id) &&
            !noDropZones.includes(status.id));

    // Определяем стили в зависимости от состояния
    const getColumnStyles = () => {
        if (!draggedTaskSourceStatus) {
            // Обычное состояние без перетаскивания
            return {
                backgroundColor: darkMode
                    ? status.color + '20'
                    : status.color + '15',
                opacity: 1,
                filter: 'none'
            };
        }

        if (!isDropAllowed) {
            // Колонка недоступна для drop
            return {
                backgroundColor: darkMode
                    ? 'rgba(128, 128, 128, 0.15)'
                    : 'rgba(128, 128, 128, 0.1)',
                opacity: 0.4,
                filter: 'grayscale(70%)',
                cursor: 'not-allowed'
            };
        }

        if (isOver) {
            // Колонка активна и над ней перетаскивают
            return {
                backgroundColor: darkMode
                    ? status.color + '50'
                    : status.color + '40',
                opacity: 1,
                filter: 'brightness(1.1)',
                borderColor: status.color,
                borderWidth: '2px',
                transform: 'scale(1.02)'
            };
        }

        // Колонка доступна для drop
        return {
            backgroundColor: darkMode
                ? status.color + '25'
                : status.color + '20',
            opacity: 1,
            filter: 'brightness(1.05)'
        };
    };

    return (
        <KanbanColumn
            statuscolor={status.color}
            darkMode={darkMode}
            sx={{
                ...getColumnStyles(),
                transition: 'all 0.3s ease',
                position: 'relative',
                flex: 1,
                minWidth: '200px',
                maxWidth: 'none',
                boxSizing: 'border-box',
                // [theme.breakpoints.down('md')]: {
                minWidth: '200px',
                flexShrink: 0,
                // },
                '&:hover': !draggedTaskSourceStatus || isDropAllowed ? {
                    backgroundColor: darkMode
                        ? status.color + '30'
                        : status.color + '25',
                    transform: 'translateY(-1px)',
                    boxShadow: darkMode
                        ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                        : '0 2px 8px rgba(0, 0, 0, 0.15)'
                } : {},
                ...sx
            }}
        >
            {/* Невидимая droppable зона, покрывающая всю колонку */}
            <Box
                ref={setNodeRef}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    pointerEvents: draggedTaskSourceStatus ? 'auto' : 'none'
                }}
            />
            {children}
        </KanbanColumn>
    );
});

// Форматирование даты с временем в формате HH:MM dd.mm.yyyy
const formatDate = (date) => {
    if (!date) return 'Не указана';

    // Если date это строка, преобразуем в объект Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Проверяем, что дата валидна
    if (isNaN(dateObj.getTime())) {
        return 'Неверная дата';
    }

    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${hours}:${minutes} ${day}.${month}.${year}`;
};

// Колонки, в которые нельзя перетаскивать задачи
const noDropZones = ['overdue'];


const KanbanView = ({
    darkMode,
    statusDefs,
    isOverdueCollapsed,
    setIsOverdueCollapsed,
    canCreateTasks,
    openCreateTaskDialog,
    tasks,
    handleEditTask,
    handleDeleteTask,
    handleViewSpecs,
    handleFocusOnTaskArea,
    canEditTask,
    canDragTask,
    canDeleteTask
}) => {
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.state.user);
    const auth = useAuth();
     // Состояние drag and drop
     const [draggedTask, setDraggedTask] = useState(null);
     // Сенсоры для drag and drop
     const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

     // Кастомный алгоритм обнаружения коллизий
     const customCollisionDetection = (args) => {
        console.log('handleDragStart', args)
        // Сначала проверяем коллизии с колонками
        const columnCollisions = pointerWithin({
            ...args,
            droppableContainers: args.droppableContainers.filter(
                container => container.data.current?.type === 'column'
            )
        });

        if (columnCollisions.length > 0) {
            return columnCollisions;
        }

        // Если нет коллизий с колонками, используем стандартный алгоритм
        return closestCorners(args);
    };

    // Обработчик начала перетаскивания
    const handleDragStart = (event) => {
        console.log('handleDragStart', event)
        const { active } = event;
        const task = active.data.current?.task;
        if (task) {
            setDraggedTask(task);
        }
    };

    // Обработчик отмены перетаскивания
    const handleDragCancel = () => {
        console.log('handleDragCancel')
        setDraggedTask(null);
    };

    // Обработчик завершения перетаскивания с обновлением статуса на сервере
    const handleDragEnd = async (event) => {
        console.log('handleDragEnd', event)
        const { active, over } = event;

        // Сбрасываем состояние перетаскивания
        setDraggedTask(null);

        if (!over) {
            return;
        }

        const activeTask = active.data.current?.task;
        const overColumn = over.data.current?.status;

        if (!activeTask || !overColumn) {
            return;
        }

        // Копии просроченных задач нельзя перетаскивать
        if (activeTask.isOverdueCopy) {
            return;
        }

        // Используем реальный статус задачи
        const sourceStatus = activeTask.status;

        // Проверить права на перетаскивание задачи
        if (!canDragTask(activeTask)) {
            return;
        }

        // Проверить, разрешен ли переход (включая запрет drop в определенные колонки)
        if (!allowedTransitions[sourceStatus]?.includes(overColumn) ||
            sourceStatus === overColumn ||
            noDropZones.includes(overColumn)) {
            return;
        }

        const updatedTask = {
            ...activeTask,
            status: overColumn,
            editor_id: currentUser.id // Добавляем информацию о редакторе
        };

        // Обновляем в Redux
        dispatch(updateTask(updatedTask));

        // Пытаемся синхронизировать с сервером
        if (auth.isAuthenticated && auth.user?.access_token && !activeTask.isLocal) {
            try {
                const response = await $authHost.put(`/apig/tasks/${activeTask.id}`, updatedTask, {
                    headers: { Authorization: `Bearer ${auth.user.access_token}` }
                });

                if (!response.data.success) {
                    throw new Error('Server returned unsuccessful response');
                }
            } catch (err) {
                console.error('Ошибка синхронизации статуса:', err);
                // Добавляем в очередь для повторной синхронизации
                dispatch(addTaskToQueue({
                    action: 'update',
                    task: updatedTask,
                    originalTaskId: activeTask.id,
                    timestamp: Date.now()
                }));
            }
        } else {
            // Локальная задача или нет авторизации - добавляем в очередь
            dispatch(addTaskToQueue({
                action: activeTask.isLocal ? 'create' : 'update',
                task: updatedTask,
                originalTaskId: activeTask.isLocal ? null : activeTask.id,
                timestamp: Date.now()
            }));
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <KanbanContainer darkMode={darkMode}>
                {/* Закрепленные заголовки колонок */}
                <KanbanHeaderRow darkMode={darkMode}>
                    {statusDefs.map(status => (
                        <Box key={`header-${status.id}`} sx={{
                            flex: status.id === 'overdue' && isOverdueCollapsed ? '0 0 35px' : 1,
                            minWidth: status.id === 'overdue' && isOverdueCollapsed ? '35px' : '200px',
                            maxWidth: 'none',
                            boxSizing: 'border-box',
                            padding: { xs: '0.1rem', md: '0.2rem' },
                            transition: 'all 0.3s ease',
                            flexShrink: 0,
                        }}>
                            <KanbanColumnHeader
                                statuscolor={status.color}
                                darkMode={darkMode}
                                onClick={() => {
                                    if (status.id === 'overdue') {
                                        setIsOverdueCollapsed(!isOverdueCollapsed);
                                    } else if (status.id === "new" && canCreateTasks) {
                                        openCreateTaskDialog();
                                    }
                                }}
                                sx={{
                                    opacity: draggedTask &&
                                        !allowedTransitions[draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status]?.includes(status.id)
                                        ? 0.5 : 1,
                                    filter: draggedTask &&
                                        !allowedTransitions[draggedTask.isOverdueCopy ? draggedTask.originalStatus : draggedTask.status]?.includes(status.id)
                                        ? 'grayscale(50%)' : 'none',
                                    cursor: (status.id === "new" && canCreateTasks) || status.id === 'overdue' ? 'pointer' : 'default',
                                    // Особое оформление для колонки просрочено
                                    ...(status.id === 'overdue' && {
                                        background: `linear-gradient(135deg, ${status.color}, ${status.color}CC)`,
                                        position: 'relative',
                                    }),
                                    ...(status.id === 'overdue' && isOverdueCollapsed && {
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    })
                                }}
                            >
                                {status.id === 'overdue' ? (
                                    isOverdueCollapsed ? (
                                        <Tooltip title="Развернуть">
                                            <VisibilityIcon sx={{ fontSize: { xs: 16, md: 18 }, opacity: 0.8 }} />
                                        </Tooltip>
                                    ) : (
                                        <>
                                            <Typography variant="body2" sx={{ color: 'inherit', fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                                                {status.title}
                                            </Typography>
                                            <Tooltip title="Свернуть">
                                                <VisibilityOffIcon sx={{ fontSize: { xs: 16, md: 18 }, opacity: 0.8, ml: 0.5 }} />
                                            </Tooltip>
                                        </>
                                    )
                                ) : (
                                    <>
                                        {status.id === "new" && canCreateTasks && <AddIcon sx={{
                                            fontSize: { xs: 16, md: 20 }
                                        }} />}
                                        <Typography variant="body2" sx={{
                                            color: 'inherit',
                                            fontSize: { xs: '0.7rem', md: '0.875rem' }
                                        }}>
                                            {status.title}
                                        </Typography>
                                    </>
                                )}
                            </KanbanColumnHeader>
                        </Box>
                    ))}
                </KanbanHeaderRow>

                {/* Контент колонок со скроллом */}
                <KanbanContentRow darkMode={darkMode}>
                    {statusDefs.map(status => (
                        <DroppableColumn
                            key={status.id}
                            status={status}
                            darkMode={darkMode}
                            draggedTask={draggedTask}
                            sx={{
                                transition: 'all 0.3s ease',
                                ...(status.id === 'overdue' && isOverdueCollapsed && {
                                    flex: '0 0 35px',
                                    minWidth: '35px',
                                    padding: '0.2rem 0',
                                    overflow: 'hidden',
                                }),
                            }}
                        >
                            {!(status.id === 'overdue' && isOverdueCollapsed) && (
                                <TasksContainer
                                    darkMode={darkMode}
                                    sx={{
                                        position: 'relative',
                                        zIndex: 2,
                                        minHeight: { xs: '200px', md: '400px' }
                                    }}
                                >
                                    <SortableContext
                                        items={tasks[status.id].map(task => task.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {tasks[status.id].map(task => (
                                            <SortableTaskCard
                                                key={task.id}
                                                task={task}
                                                isDragging={draggedTask?.id === task.id}
                                                onEdit={handleEditTask}
                                                onDelete={handleDeleteTask}
                                                onViewSpecs={handleViewSpecs}
                                                onFocusArea={handleFocusOnTaskArea}
                                                darkMode={darkMode}
                                                canEdit={canEditTask(task)}
                                                canDrag={canDragTask(task, status.id)}
                                                canDelete={canDeleteTask(task)}
                                            />
                                        ))}
                                    </SortableContext>
                                </TasksContainer>
                            )}
                        </DroppableColumn>
                    ))}
                </KanbanContentRow>
            </KanbanContainer>

            <DragOverlay dropAnimation={null}>
                {draggedTask && (
                    <div role="none" aria-hidden="true" style={{ pointerEvents: 'none' }}>
                        <TaskCard darkMode={darkMode} sx={{
                            opacity: 0.8,
                            transform: 'rotate(5deg)',
                            display: 'flex',
                            flexDirection: 'column',
                            pointerEvents: 'none',
                            '& *': { pointerEvents: 'none', tabIndex: -1 }
                        }}>
                            <TaskName darkMode={darkMode}>{draggedTask.title || draggedTask.name}</TaskName>
                            {(draggedTask.description || draggedTask.desc) && (
                                <TaskDesc
                                    darkMode={darkMode}
                                    sx={{
                                        '& strong, & b': {
                                            fontWeight: 700,
                                            color: darkMode ? '#fff' : '#1a1a1a'
                                        },
                                        '& em, & i': {
                                            fontStyle: 'italic',
                                            color: darkMode ? '#e0e0e0' : '#424242'
                                        },
                                        '& ul, & ol': {
                                            paddingLeft: 2,
                                            margin: 0,
                                            color: darkMode ? '#bbb' : '#666'
                                        },
                                        '& li': {
                                            listStyle: 'disc',
                                            marginBottom: '2px',
                                            lineHeight: 1.2
                                        },
                                        '& code': {
                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                            padding: '0.1rem 0.2rem',
                                            borderRadius: '0.2rem',
                                            fontFamily: 'monospace',
                                            fontSize: '0.8em',
                                            color: darkMode ? '#ff6b6b' : '#d63384'
                                        },
                                        flex: 1,
                                        pointerEvents: 'none'
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: (draggedTask.description || draggedTask.desc)
                                            // Базовая обработка для DragOverlay (упрощенная)
                                            .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                            .replace(/`([^`]+)`/g, '<code>$1</code>')
                                            .replace(/^- (.+)$/gm, '<li>$1</li>')
                                            .replace(/(<li>.*<\/li>\s*)+/g, (match) => `<ul>${match}</ul>`)
                                            .replace(/\n/g, '<br>')
                                    }}
                                />
                            )}

                            {/* ID задачи в DragOverlay */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mt: 1,
                                pt: 1,
                                borderTop: darkMode
                                    ? '1px solid rgba(255,255,255,0.08)'
                                    : '1px solid rgba(0,0,0,0.08)',
                                pointerEvents: 'none'
                            }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: darkMode ? '#888' : '#666',
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        fontFamily: 'monospace',
                                        opacity: 0.8,
                                        pointerEvents: 'none'
                                    }}
                                >
                                    #{draggedTask.isOverdueCopy ? draggedTask.originalId : draggedTask.id}
                                </Typography>
                            </Box>
                        </TaskCard>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
};

export default KanbanView;
